import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm, Typography, List, InputNumber, Card, Alert } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { ViewService } from '../../Services/ViewService';
import { MemberFieldService } from '../../Services/MemberFieldService';
import { CustomListService } from '../../Services/CustomListService';
import { CustomListFieldService } from '../../Services/CustomLifFieldService';
import { WebApiModelsView, WebApiViewDtoCreateViewDto, WebApiViewDtoUpdateViewDto, WebApiModelsMemberField, WebApiViewDtoCreateViewColumnDto, WebApiModelsEnumerationTypesMemberFieldTypes, WebApiCustomListsDtoCustomListDto, WebApiModelsCustomListField } from '../../Repository/eventbookyapi';
import { TextService } from '../../Services/TextService';
import LoadingSpinner from '../../Components/Shared/LoadingSpinner';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import LeftSettings from '../Account/LeftSettings';
import { mapDynamicInputLabel } from '../../Mappers/mapDynamicInputLabel';
import { sortAlphabetically, sortDate } from '../../Helpers/TableSortHelper';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';
import { ViewType } from '../../Types/ViewType';

const { Title } = Typography;
const { Option } = Select;

interface ViewColumn {
    sortOrder: number;
    columnType?: WebApiModelsEnumerationTypesMemberFieldTypes;
    customFieldId?: number | null;
    fieldName?: string;
}

const Views: React.FC = () => {
    const [views, setViews] = useState<WebApiModelsView[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingView, setEditingView] = useState<WebApiModelsView | null>(null);
    const [memberFields, setMemberFields] = useState<WebApiModelsMemberField[]>([]);
    const [customLists, setCustomLists] = useState<WebApiCustomListsDtoCustomListDto[]>([]);
    const [customListFields, setCustomListFields] = useState<WebApiModelsCustomListField[]>([]);
    const [selectedViewType, setSelectedViewType] = useState<number>(0);
    const [selectedCustomListId, setSelectedCustomListId] = useState<number | null>(null);
    const [viewColumns, setViewColumns] = useState<ViewColumn[]>([]);
    const [form] = Form.useForm();
    const GetText = TextService.GetText;

    const ViewTypeOptions = [
        { value: ViewType.Members, label: GetText('Views_ViewTypeMember') },
        { value: ViewType.CustomList, label: GetText('Views_ViewTypeCustomList') },
        { value: ViewType.PublicSiteMembers, label: GetText('Views_ViewTypePublicSiteMembers') || 'Public Site Members' }
    ];

    // Load views and member fields on component mount
    useEffect(() => {
        loadViews();
        loadMemberFields();
        loadCustomLists();
    }, []);

    const loadViews = async () => {
        try {
            setLoading(true);
            const response = await ViewService.GetAllViews();
            setViews(response);
        } catch (error) {
            console.error('Error loading views:', error);
            message.error(GetText('Views_FailedToLoad'));
        } finally {
            setLoading(false);
        }
    };

    const loadMemberFields = async () => {
        try {
            const fields = await MemberFieldService.GetMemberFields();

            // Add standard fields that aren't returned from the API
            const standardFields: WebApiModelsMemberField[] = [
                {
                    id: 17,
                    columnType: MemberFieldTypes.Group as any,
                    nameAdmin: GetText('MemberFields_Group'),
                    namePublic: GetText('MemberFields_Group'),
                    visibleInAdmin: true,
                    visiblePublic: false
                } as WebApiModelsMemberField
            ];

            // Combine standard fields with API fields
            setMemberFields([...standardFields, ...fields]);
        } catch (error) {
            console.error('Error loading member fields:', error);
            message.error(GetText('Views_FailedToLoadFields'));
        }
    };

    const loadCustomLists = async () => {
        try {
            const lists = await CustomListService.GetCustomLists();
            setCustomLists(lists);
        } catch (error) {
            console.error('Error loading custom lists:', error);
            message.error(GetText('Views_FailedToLoadCustomLists') || 'Failed to load custom lists');
        }
    };

    const loadCustomListFields = async (listId: number) => {
        try {
            const fields = await CustomListFieldService.GetCCustomListFields(listId);
            setCustomListFields(fields);
        } catch (error) {
            console.error('Error loading custom list fields:', error);
            message.error(GetText('Views_FailedToLoadCustomListFields') || 'Failed to load custom list fields');
        }
    };

    const handleCreate = () => {
        setEditingView(null);
        setViewColumns([]);
        setSelectedViewType(ViewType.Members);
        setSelectedCustomListId(null);
        setCustomListFields([]);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (view: WebApiModelsView) => {
        setEditingView(view);
        setSelectedViewType(view.viewType || 0);
        setSelectedCustomListId(view.customListId || null);
        form.setFieldsValue({
            name: view.name,
            viewType: view.viewType,
            customListId: view.customListId
        });

        // Load custom list fields if this is a custom list view
        if (view.viewType === ViewType.CustomList && view.customListId) {
            loadCustomListFields(view.customListId);
        }

        // Load existing view columns
        const columns: ViewColumn[] = (view.viewColumns || []).map(col => {
            const fieldName = mapDynamicInputLabel(col.columnType as any, memberFields, col.customFieldId);
            return {
                sortOrder: col.sortOrder || 0,
                columnType: col.columnType,
                customFieldId: col.customFieldId,
                fieldName: fieldName || undefined
            };
        }).sort((a, b) => a.sortOrder - b.sortOrder);

        setViewColumns(columns);
        setModalVisible(true);
    };

    const handleDelete = async (viewId: number) => {
        try {
            await ViewService.DeleteView(viewId);
            message.success(GetText('Views_DeletedSuccessfully'));
            loadViews();
        } catch (error) {
            console.error('Error deleting view:', error);
            message.error(GetText('Views_FailedToDelete'));
        }
    };

    const handleViewTypeChange = (value: number) => {
        setSelectedViewType(value);
        setViewColumns([]);
        if (value === ViewType.Members || value === ViewType.PublicSiteMembers) {
            // Member view or Public Site Members - clear custom list selection
            form.setFieldsValue({ customListId: undefined });
            setSelectedCustomListId(null);
            setCustomListFields([]);
        } else if (value === ViewType.CustomList) {
            // Custom list view - require custom list selection
            form.setFieldsValue({ customListId: undefined });
            setSelectedCustomListId(null);
            setCustomListFields([]);
        }
    };

    const handleCustomListChange = async (value: number) => {
        setSelectedCustomListId(value);
        setViewColumns([]);
        if (value) {
            await loadCustomListFields(value);
        } else {
            setCustomListFields([]);
        }
    };

    const handleAddColumn = (fieldId: number) => {
        let fieldName: string | undefined;
        let columnType: any;
        let customFieldId: number | null = null;

        if (selectedViewType === ViewType.CustomList) {
            // Custom list view - use custom list fields
            const customField = customListFields.find(f => f.id === fieldId);
            if (!customField) return;
            fieldName = customField.nameAdmin || undefined;
            columnType = MemberFieldTypes.Custom;
            customFieldId = fieldId;
        } else {
            // Member view - use member fields
            const memberField = memberFields.find(f => f.id === fieldId);
            if (!memberField) return;
            fieldName = mapDynamicInputLabel(memberField.columnType as any, memberFields, memberField.id) ?? undefined;
            columnType = memberField.columnType;
            customFieldId = memberField.columnType === MemberFieldTypes.Custom ? fieldId : null;
        }

        const newColumn: ViewColumn = {
            sortOrder: viewColumns.length,
            columnType: columnType,
            customFieldId: customFieldId,
            fieldName: fieldName
        };

        setViewColumns([...viewColumns, newColumn]);
    };

    const handleRemoveColumn = (index: number) => {
        const newColumns = viewColumns.filter((_, i) => i !== index);
        // Re-index sortOrder
        newColumns.forEach((col, i) => col.sortOrder = i);
        setViewColumns(newColumns);
    };

    const handleMoveColumn = (index: number, direction: 'up' | 'down') => {
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === viewColumns.length - 1)) {
            return;
        }

        const newColumns = [...viewColumns];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        [newColumns[index], newColumns[targetIndex]] = [newColumns[targetIndex], newColumns[index]];

        // Update sortOrder
        newColumns.forEach((col, i) => col.sortOrder = i);
        setViewColumns(newColumns);
    };

    const handleSubmit = async (values: any) => {
        try {
            // Validate that only one PublicSiteMembers view exists
            if (values.viewType === ViewType.PublicSiteMembers) {
                const existingPublicSiteView = views.find(v =>
                    v.viewType === ViewType.PublicSiteMembers &&
                    (!editingView || v.id !== editingView.id)
                );
                if (existingPublicSiteView) {
                    message.error(GetText('Views_PublicSiteMembersAlreadyExists') || 'A Public Site Members view already exists. Only one is allowed.');
                    return;
                }
            }

            const viewColumnsDto: WebApiViewDtoCreateViewColumnDto[] = viewColumns.map(col => ({
                sortOrder: col.sortOrder,
                columnType: col.columnType,
                customFieldId: col.customFieldId
            }));

            if (editingView) {
                // Update existing view
                const updateData: WebApiViewDtoUpdateViewDto = {
                    name: values.name,
                    viewType: values.viewType,
                    customListId: values.customListId || null,
                    viewColumns: viewColumnsDto
                };
                await ViewService.UpdateView(editingView.id!, updateData);
                message.success(GetText('Views_UpdatedSuccessfully'));
            } else {
                // Create new view
                const createData: WebApiViewDtoCreateViewDto = {
                    name: values.name,
                    viewType: values.viewType,
                    customListId: values.customListId || null,
                    viewColumns: viewColumnsDto
                };
                await ViewService.CreateView(createData);
                message.success(GetText('Views_CreatedSuccessfully'));
            }

            setModalVisible(false);
            form.resetFields();
            setViewColumns([]);
            loadViews();
        } catch (error) {
            console.error('Error saving view:', error);
            message.error(GetText('Views_FailedToSave'));
        }
    };

    const columns = [
        {
            title: GetText('Views_ColumnID'),
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: GetText('Views_ColumnName'),
            dataIndex: 'name',
            key: 'name',
            width: 250,
            sorter: (a: WebApiModelsView, b: WebApiModelsView) =>
                sortAlphabetically(a, b, 'name'),
        },
        {
            title: GetText('Views_ColumnCustomList'),
            dataIndex: 'customListId',
            key: 'customListId',
            width: 180,
            render: (customListId: number | null) => {
                if (!customListId) return '-';
                const customList = customLists.find(cl => cl.id === customListId);
                return customList?.name || `ID: ${customListId}`;
            },
        },
        {
            title: GetText('Views_ColumnViewType'),
            dataIndex: 'viewType',
            key: 'viewType',
            width: 150,
            render: (viewType: number) => {
                const option = ViewTypeOptions.find(opt => opt.value === viewType);
                return option?.label || `Type ${viewType}`;
            },
            filters: ViewTypeOptions.map(option => ({
                text: option.label,
                value: option.value
            })),
            onFilter: (value: boolean | React.Key, record: WebApiModelsView) =>
                record.viewType === value,
        },
        {
            title: GetText('Views_ColumnCreatedAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (date: Date) => date ? new Date(date).toLocaleString() : '-',
            sorter: (a: WebApiModelsView, b: WebApiModelsView) =>
                sortDate(a.createdAt, b.createdAt),
        },
        {
            title: GetText('Views_ColumnUpdatedAt'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 180,
            render: (date: Date | null) => date ? new Date(date).toLocaleString() : '-',
        },
        {
            title: GetText('Views_ColumnActions'),
            key: 'actions',
            width: 150,
            fixed: 'right' as const,
            render: (_: any, record: WebApiModelsView) => (
                <Space size="middle">
                    <EditOutlined
                        style={{ fontSize: "20px", cursor: "pointer", color: "#1890ff" }}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title={GetText('Views_DeleteConfirmTitle')}
                        description={GetText('Views_DeleteConfirmDescription')}
                        onConfirm={() => handleDelete(record.id!)}
                        okText={GetText('Common_Yes')}
                        cancelText={GetText('Common_No')}
                    >
                        <DeleteOutlined
                            style={{ color: "red", fontSize: "20px", cursor: "pointer" }}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (loading) {
        return (
            <LayoutLeftMenu title={GetText("Views_Title")} menu={<LeftSettings selected={"views"} />}>
                <LoadingSpinner loadingText={GetText('Views_LoadingText')} />
            </LayoutLeftMenu>
        );
    }

    return (
        <LayoutLeftMenu title={GetText("Views_Title")} menu={<LeftSettings selected={"views"} />}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    {GetText('Views_AddNewView')}
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={views}
                rowKey="id"
                scroll={{ x: 1200 }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
            />

            <Modal
                title={editingView ? GetText('Views_ModalTitleEdit') : GetText('Views_ModalTitleCreate')}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                    setViewColumns([]);
                }}
                onOk={() => form.submit()}
                okText={editingView ? GetText('Views_ButtonUpdate') : GetText('Views_ButtonCreate')}
                cancelText={GetText('Views_ButtonCancel')}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        label={GetText('Views_LabelViewName')}
                        name="name"
                        rules={[
                            { required: true, message: GetText('Views_ValidationViewNameRequired') },
                            { min: 2, message: GetText('Views_ValidationViewNameMin') }
                        ]}
                    >
                        <Input placeholder={GetText('Views_PlaceholderViewName')} />
                    </Form.Item>

                    <Form.Item
                        label={GetText('Views_LabelViewType')}
                        name="viewType"
                        rules={[
                            { required: true, message: GetText('Views_ValidationViewTypeRequired') }
                        ]}
                        initialValue={ViewType.Members}
                    >
                        <Select
                            style={{ width: '100%' }}
                            placeholder={GetText('Views_PlaceholderViewType')}
                            onChange={handleViewTypeChange}
                        >
                            {ViewTypeOptions.map(option => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {selectedViewType === ViewType.PublicSiteMembers && (
                        <Alert
                            message={GetText('Views_PublicSiteMembersInfoTitle') || 'Member Search Enabled'}
                            description={GetText('Views_PublicSiteMembersInfoDescription') || 'By creating this view, you will enable your members to search for other members when they are logged in. Only the fields you add to this view will be visible to them.'}
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {selectedViewType === ViewType.CustomList && (
                        <Form.Item
                            label={GetText('Views_LabelCustomListId')}
                            name="customListId"
                            help={GetText('Views_HelpCustomListId')}
                            rules={[
                                { required: true, message: GetText('Views_ValidationCustomListRequired') || 'Please select a custom list' }
                            ]}
                        >
                            <Select
                                style={{ width: '100%' }}
                                placeholder={GetText('Views_PlaceholderCustomListId')}
                                onChange={handleCustomListChange}
                            >
                                {customLists.map(list => (
                                    <Option key={list.id} value={list.id}>
                                        {list.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Card title={GetText('Views_CardTitleViewColumns')} size="small" style={{ marginTop: 16 }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {selectedViewType === ViewType.CustomList && !selectedCustomListId ? (
                                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                    {GetText('Views_SelectCustomListFirst') || 'Please select a custom list first'}
                                </div>
                            ) : (
                                <>
                                    <Select
                                        style={{ width: '100%' }}
                                        placeholder={GetText('Views_PlaceholderSelectField')}
                                        onChange={handleAddColumn}
                                        value={undefined}
                                        disabled={selectedViewType === ViewType.CustomList && !selectedCustomListId}
                                    >
                                        {selectedViewType === ViewType.CustomList
                                            ? customListFields.map(field => (
                                                <Option key={field.id} value={field.id}>
                                                    {field.nameAdmin}
                                                </Option>
                                            ))
                                            : memberFields.map(field => (
                                                <Option key={field.id} value={field.id}>
                                                    {mapDynamicInputLabel(field.columnType as any, memberFields, field.id)}
                                                </Option>
                                            ))
                                        }
                                    </Select>

                                    {viewColumns.length > 0 ? (
                                        <List
                                            size="small"
                                            bordered
                                            dataSource={viewColumns}
                                            renderItem={(column, index) => (
                                                <List.Item
                                                    actions={[
                                                        <Button
                                                            key="up"
                                                            size="small"
                                                            icon={<ArrowUpOutlined />}
                                                            onClick={() => handleMoveColumn(index, 'up')}
                                                            disabled={index === 0}
                                                        />,
                                                        <Button
                                                            key="down"
                                                            size="small"
                                                            icon={<ArrowDownOutlined />}
                                                            onClick={() => handleMoveColumn(index, 'down')}
                                                            disabled={index === viewColumns.length - 1}
                                                        />,
                                                        <Button
                                                            key="delete"
                                                            size="small"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => handleRemoveColumn(index)}
                                                        />
                                                    ]}
                                                >
                                                    <Space>
                                                        <span style={{ fontWeight: 'bold' }}>{index + 1}.</span>
                                                        <span>{column.fieldName}</span>
                                                        <span style={{ color: '#999' }}>({GetText('Views_ColumnOrderLabel')}: {column.sortOrder})</span>
                                                    </Space>
                                                </List.Item>
                                            )}
                                        />
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                            {GetText('Views_NoColumnsText')}
                                        </div>
                                    )}
                                </>
                            )}
                        </Space>
                    </Card>
                </Form>
            </Modal>
        </LayoutLeftMenu>
    );
};

export default Views;
